import * as XLSX from 'xlsx';

import { statementAnalyzerApi } from '../api';
import { store } from '../store';

import { formatCurrency, formatPreciseFee } from './utils';

import type {
	UnifiedAnalysisData,
	UnifiedFeeItem,
	UnifiedOtherService,
} from '../types';

export interface ExportData {
	section: string;
	description: string;
	currentProcessor: {
		count: number;
		rate: string;
		fee: number;
		perTrans: number;
		volume: number;
		feeDue: number;
	};
	comparisonProcessor: {
		count: number;
		rate: string;
		fee: number;
		perTrans: number;
		volume: number;
		feeDue: number;
	};
	savings: number;
}

export class ExportService {
	static transformAnalysisDataToExportData(
		analysisData: UnifiedAnalysisData,
	): ExportData[] {
		const exportData: ExportData[] = [];

		// Helper function to transform fee items
		const transformFeeItems = (
			items: (UnifiedFeeItem | UnifiedOtherService)[],
			section: string,
		) => {
			items.forEach((item) => {
				const isOtherService = !('rate' in (item as any).ic);
				const feeItem = item as UnifiedFeeItem;
				const otherItem = item as UnifiedOtherService;

				const currentProcessor = isOtherService
					? {
							count: otherItem.ic.count,
							rate: '-',
							fee: otherItem.ic.fee,
							perTrans: 0,
							volume: 0,
							feeDue: otherItem.ic.feeDue,
						}
					: {
							count: feeItem.ic.count,
							rate: feeItem.ic.rate,
							fee: feeItem.ic.fee || 0,
							perTrans: feeItem.ic.perTrans || 0,
							volume: feeItem.ic.volume || 0,
							feeDue: feeItem.ic.feeDue,
						};

				const comparisonProcessor = isOtherService
					? {
							count: otherItem.icPlus.count,
							rate: '-',
							fee: otherItem.icPlus.fee,
							perTrans: 0,
							volume: 0,
							feeDue: otherItem.icPlus.feeDue,
						}
					: {
							count: feeItem.icPlus.count,
							rate: feeItem.icPlus.rate,
							fee: feeItem.icPlus.fee || 0,
							perTrans: feeItem.icPlus.perTrans || 0,
							volume: feeItem.icPlus.volume || 0,
							feeDue: feeItem.icPlus.feeDue,
						};

				const savings = currentProcessor.feeDue - comparisonProcessor.feeDue;

				exportData.push({
					section,
					description: item.type,
					currentProcessor,
					comparisonProcessor,
					savings,
				});
			});
		};

		// Transform each section
		transformFeeItems(
			analysisData.duesFeesAssessments,
			'DUES, FEES & ASSESSMENTS',
		);
		transformFeeItems(analysisData.interchange, 'INTERCHANGE');
		transformFeeItems(
			analysisData.authorizationAndDiscount,
			'AUTHORIZATION & DISCOUNT FEES',
		);
		transformFeeItems(analysisData.otherFees, 'OTHER FEES');

		return exportData;
	}

	static exportToCSV(
		analysisData: UnifiedAnalysisData,
		filename: string = 'analysis-data.csv',
	): void {
		const exportData = this.transformAnalysisDataToExportData(analysisData);

		// Create CSV headers
		const headers = [
			'Section',
			'Description',
			'Current Processor Count',
			'Current Processor Rate',
			'Current Processor Fee',
			'Current Processor Per Transaction',
			'Current Processor Volume',
			'Current Processor Fee Due',
			'Comparison Processor Count',
			'Comparison Processor Rate',
			'Comparison Processor Fee',
			'Comparison Processor Per Transaction',
			'Comparison Processor Volume',
			'Comparison Processor Fee Due',
			'Savings',
		];

		// Create CSV rows with section separators
		const csvRows: any[] = [];
		let currentSection = '';

		exportData.forEach((row) => {
			// Add section separator if section changed
			if (row.section !== currentSection) {
				if (currentSection !== '') {
					csvRows.push(['']); // Empty row for separation
				}
				csvRows.push([`=== ${row.section} ===`]); // Section header
				csvRows.push(['']); // Empty row after section header
				currentSection = row.section;
			}

			csvRows.push([
				row.section,
				row.description,
				row.currentProcessor.count,
				row.currentProcessor.rate,
				formatPreciseFee(row.currentProcessor.fee),
				formatPreciseFee(row.currentProcessor.perTrans),
				formatCurrency(row.currentProcessor.volume),
				formatCurrency(row.currentProcessor.feeDue),
				row.comparisonProcessor.count,
				row.comparisonProcessor.rate,
				formatPreciseFee(row.comparisonProcessor.fee),
				formatPreciseFee(row.comparisonProcessor.perTrans),
				formatCurrency(row.comparisonProcessor.volume),
				formatCurrency(row.comparisonProcessor.feeDue),
				formatCurrency(row.savings),
			]);
		});

		// Add summary at the end
		csvRows.push(['']); // Empty row
		csvRows.push(['=== SUMMARY ===']);
		csvRows.push([
			'Current Processor Total',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.ic),
		]);
		csvRows.push([
			'Comparison Processor Total',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.icPlus),
		]);
		csvRows.push([
			'Total Savings',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.ic - analysisData.totals.icPlus),
		]);

		// Combine headers and rows
		const csvContent = [headers, ...csvRows]
			.map((row) => row.map((cell: any) => `"${cell}"`).join(','))
			.join('\n');

		// Create and download file
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', filename);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	static exportToXLSX(
		analysisData: UnifiedAnalysisData,
		filename: string = 'analysis-data.xlsx',
	): void {
		const exportData = this.transformAnalysisDataToExportData(analysisData);

		// Create worksheet data with section separation
		const worksheetData = [
			// Headers
			[
				'Section',
				'Description',
				'Current Processor Count',
				'Current Processor Rate',
				'Current Processor Fee',
				'Current Processor Per Transaction',
				'Current Processor Volume',
				'Current Processor Fee Due',
				'Comparison Processor Count',
				'Comparison Processor Rate',
				'Comparison Processor Fee',
				'Comparison Processor Per Transaction',
				'Comparison Processor Volume',
				'Comparison Processor Fee Due',
				'Savings',
			],
		];

		// Add data rows with section separators
		let currentSection = '';
		exportData.forEach((row) => {
			// Add section separator if section changed
			if (row.section !== currentSection) {
				if (currentSection !== '') {
					worksheetData.push(['']); // Empty row for separation
				}
				worksheetData.push([
					`=== ${row.section} ===`,
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
				]); // Section header
				worksheetData.push(['']); // Empty row after section header
				currentSection = row.section;
			}

			worksheetData.push([
				row.section,
				row.description,
				row.currentProcessor.count.toString(),
				row.currentProcessor.rate,
				row.currentProcessor.fee.toString(),
				row.currentProcessor.perTrans.toString(),
				row.currentProcessor.volume.toString(),
				row.currentProcessor.feeDue.toString(),
				row.comparisonProcessor.count.toString(),
				row.comparisonProcessor.rate,
				row.comparisonProcessor.fee.toString(),
				row.comparisonProcessor.perTrans.toString(),
				row.comparisonProcessor.volume.toString(),
				row.comparisonProcessor.feeDue.toString(),
				row.savings.toString(),
			]);
		});

		// Add summary at the end
		worksheetData.push(['']); // Empty row
		worksheetData.push([
			'=== SUMMARY ===',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);
		worksheetData.push([
			'Current Processor Total',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.ic),
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);
		worksheetData.push([
			'Comparison Processor Total',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.icPlus),
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);
		worksheetData.push([
			'Total Savings',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.ic - analysisData.totals.icPlus),
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);

		// Create workbook and worksheet
		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

		// Set column widths
		const columnWidths = [
			{ wch: 25 }, // Section
			{ wch: 35 }, // Description
			{ wch: 12 }, // Current Processor Count
			{ wch: 12 }, // Current Processor Rate
			{ wch: 15 }, // Current Processor Fee
			{ wch: 15 }, // Current Processor Per Transaction
			{ wch: 15 }, // Current Processor Volume
			{ wch: 15 }, // Current Processor Fee Due
			{ wch: 12 }, // Comparison Processor Count
			{ wch: 12 }, // Comparison Processor Rate
			{ wch: 15 }, // Comparison Processor Fee
			{ wch: 15 }, // Comparison Processor Per Transaction
			{ wch: 15 }, // Comparison Processor Volume
			{ wch: 15 }, // Comparison Processor Fee Due
			{ wch: 15 }, // Savings
		];
		worksheet['!cols'] = columnWidths;

		// Add worksheet to workbook
		XLSX.utils.book_append_sheet(workbook, worksheet, 'All Analysis Data');

		// Generate and download file
		XLSX.writeFile(workbook, filename);
	}

	static exportSummaryToXLSX(
		analysisData: UnifiedAnalysisData,
		filename: string = 'analysis-summary.xlsx',
	): void {
		const workbook = XLSX.utils.book_new();

		// Create summary sheet
		const summaryData = [
			['Analysis Summary'],
			[''],
			['Processing Month', analysisData.processingMonth],
			['Current Processor', analysisData.processorName],
			['Comparison Processor', analysisData.processorNamePlus],
			[''],
			['Totals'],
			['Current Processor Total', formatCurrency(analysisData.totals.ic)],
			[
				'Comparison Processor Total',
				formatCurrency(analysisData.totals.icPlus),
			],
			[
				'Total Savings',
				formatCurrency(analysisData.totals.ic - analysisData.totals.icPlus),
			],
			[''],
			['Generated on', new Date().toLocaleString()],
		];

		const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
		summarySheet['!cols'] = [{ wch: 20 }, { wch: 30 }];
		XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

		// Create detailed data sheet with section separation
		const exportData = this.transformAnalysisDataToExportData(analysisData);
		const detailedData = [
			// Headers
			[
				'Section',
				'Description',
				'Current Processor Count',
				'Current Processor Rate',
				'Current Processor Fee',
				'Current Processor Per Transaction',
				'Current Processor Volume',
				'Current Processor Fee Due',
				'Comparison Processor Count',
				'Comparison Processor Rate',
				'Comparison Processor Fee',
				'Comparison Processor Per Transaction',
				'Comparison Processor Volume',
				'Comparison Processor Fee Due',
				'Savings',
			],
		];

		// Add data rows with section separators
		let currentSection = '';
		exportData.forEach((row) => {
			// Add section separator if section changed
			if (row.section !== currentSection) {
				if (currentSection !== '') {
					detailedData.push(['']); // Empty row for separation
				}
				detailedData.push([
					`=== ${row.section} ===`,
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
					'',
				]); // Section header
				detailedData.push(['']); // Empty row after section header
				currentSection = row.section;
			}

			detailedData.push([
				row.section,
				row.description,
				row.currentProcessor.count.toString(),
				row.currentProcessor.rate,
				row.currentProcessor.fee.toString(),
				row.currentProcessor.perTrans.toString(),
				row.currentProcessor.volume.toString(),
				row.currentProcessor.feeDue.toString(),
				row.comparisonProcessor.count.toString(),
				row.comparisonProcessor.rate,
				row.comparisonProcessor.fee.toString(),
				row.comparisonProcessor.perTrans.toString(),
				row.comparisonProcessor.volume.toString(),
				row.comparisonProcessor.feeDue.toString(),
				row.savings.toString(),
			]);
		});

		// Add summary at the end
		detailedData.push(['']); // Empty row
		detailedData.push([
			'=== SUMMARY ===',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);
		detailedData.push([
			'Current Processor Total',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.ic),
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);
		detailedData.push([
			'Comparison Processor Total',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.icPlus),
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);
		detailedData.push([
			'Total Savings',
			'',
			'',
			'',
			'',
			'',
			'',
			formatCurrency(analysisData.totals.ic - analysisData.totals.icPlus),
			'',
			'',
			'',
			'',
			'',
			'',
			'',
		]);

		const detailedSheet = XLSX.utils.aoa_to_sheet(detailedData);
		detailedSheet['!cols'] = [
			{ wch: 25 },
			{ wch: 35 },
			{ wch: 12 },
			{ wch: 12 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 12 },
			{ wch: 12 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
			{ wch: 15 },
		];
		XLSX.utils.book_append_sheet(workbook, detailedSheet, 'All Analysis Data');

		// Generate and download file
		XLSX.writeFile(workbook, filename);
	}

	static async exportToPDF(
		analysisData: UnifiedAnalysisData,
		filename: string = 'analysis-data.pdf',
		useBackend: boolean = true,
		fileId?: string,
		viewType: 'summary' | 'full' = 'summary',
		viewMode: 'default' | 'user_edits' | 'customized_quote' = 'default',
	): Promise<void> {
		if (!useBackend || !fileId) {
			throw new Error('Backend PDF export requires a valid file ID.');
		}
		const mutation = store.dispatch(
			statementAnalyzerApi.endpoints.exportPDF.initiate({
				fileId,
				filename,
				view_type: viewType,
				view_mode: viewMode,
			}),
		);

		try {
			const blob = await mutation.unwrap();

			if (!(blob instanceof Blob)) {
				throw new Error('PDF export returned an unexpected response.');
			}

			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.setAttribute('href', url);
			link.setAttribute('download', filename);
			link.style.visibility = 'hidden';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} finally {
			mutation.reset();
		}
	}
}
